import { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import './destinationDetails.css';

export const DestinationDetails = ({ destination, id, language = 'tr', copyable = false }) => {
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setCopied('');
    setError('');
  }, [destination?.id, destination?.version]);

  if (!destination) return null;

  const tr = language === 'tr';
  const bank = destination.kind === 'bank';

  const copy = async (key, value, label) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(key);
      setError('');
      toast.success(tr ? `${label} kopyalandı` : `${label} copied`);
      setTimeout(() => {
        setCopied(prev => (prev === key ? '' : prev));
      }, 2500);
    } catch {
      setError(tr ? 'Kopyalanamadı. Manuel olarak kopyalayabilirsiniz.' : 'Copy failed. Select and copy manually.');
    }
  };

  const fields = [
    ['provider', bank ? (tr ? 'Banka' : 'Bank') : (tr ? 'Coin / Ağ' : 'Coin / Network'), bank ? destination.bank_name : `${destination.currency} · ${destination.network}`],
    ['holder', tr ? 'Hesap Sahibi' : 'Account holder', destination.account_holder],
    ['address', bank ? 'IBAN' : tr ? 'Cüzdan Adresi' : 'Wallet address', destination.address],
    ...(destination.memo ? [['memo', 'Memo / Tag', destination.memo]] : [])
  ];

  return (
    <section className="deposit-destination-details" data-testid={`${id}-details`}>
      <h2>{tr ? 'Firma Alıcı Hesabı' : 'Company receiving account'}</h2>
      <dl>
        {fields.map(([key, label, value]) => (
          <div key={key}>
            <dt>{label}</dt>
            <dd data-testid={`${id}-${key}`}>
              <span className={key === 'address' ? 'destination-address' : key === 'holder' ? 'destination-holder' : ''}>{value}</span>
              {copyable && ['holder', 'address', 'memo'].includes(key) && Boolean(value) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  data-testid={`${id}-copy-${key}`}
                  aria-label={`${label} ${tr ? 'kopyala' : 'copy'}`}
                  title={tr ? 'Kopyala' : 'Copy'}
                  onClick={() => copy(key, value, label)}
                >
                  {copied === key ? <Check size={15} /> : <Copy size={15} />}
                </Button>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {copyable && (
        <>
          <p className="destination-limit" data-testid={`${id}-limits`}>
            {tr ? 'Yatırım Aralığı' : 'Deposit limits'}: {destination.min_amount} – {destination.max_amount} {destination.currency}
          </p>
          {!bank && (
            <p className="destination-network-warning" data-testid={`${id}-network-warning`}>
              {tr ? `Yalnızca ${destination.currency} · ${destination.network}. Farklı coin veya ağ kullanmayın.` : `Only ${destination.currency} · ${destination.network}. Do not use a different coin or network.`}
            </p>
          )}
        </>
      )}
      {copied && <span role="status" data-testid={`${id}-copied`}>{tr ? 'Kopyalandı' : 'Copied'}</span>}
      {error && <p role="alert" data-testid={`${id}-copy-error`}>{error}</p>}
    </section>
  );
};